import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';

// The locale must be resolved before the first render so no frame is painted in
// the wrong language (section 10.1). Importing this module initialises i18next
// as a side effect, with the language already chosen.
import './shared/i18n';
import './styles/index.css';

import { App } from './App';
import { ThemeProvider } from './app/ThemeProvider';

/**
 * React Query.
 *
 * `retry: 1` rather than the default three: a lookup already retries inside the
 * server, and a fourth attempt from the browser turns a visibly slow screen into
 * an apparently broken one. A 4xx is never retried — it will not fix itself, and
 * retrying it burns the rate-limit budget.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status ?? 0;
        if (status >= 400 && status < 500) return false;
        return failureCount < 1;
      },
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const container = document.getElementById('root');
if (!container) {
  // Without this the failure is a blank page and a console message nobody reads.
  throw new Error('Missing #root element: index.html is out of sync with main.tsx.');
}

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
);
